import { Shield, Heart, Search, Compass, Star } from "lucide-react";

interface HealingStage {
  stage: number;
  stage_name: string;
  rationale: string;
}

interface Props {
  healingStage: HealingStage;
}

const STAGES = [
  {
    num: 0,
    label: "Crisis Intervention",
    shortLabel: "Crisis",
    description: "Stabilize acute distress, ensure safety, and address immediate psychological crisis before deeper work begins.",
    icon: Shield,
    color: "hsl(0, 84%, 60%)",       // red
    bgClass: "bg-destructive",
    textClass: "text-destructive",
  },
  {
    num: 1,
    label: "Harmonize Mind & Body",
    shortLabel: "Harmonize",
    description: "Restore somatic regulation, rebuild body awareness, and synchronize nervous system rhythms for emotional stability.",
    icon: Heart,
    color: "hsl(38, 92%, 50%)",      // amber
    bgClass: "bg-amber-500",
    textClass: "text-amber-500",
  },
  {
    num: 2,
    label: "Analysis & Investigation",
    shortLabel: "Analysis",
    description: "Explore root causes through cognitive patterns, subconscious beliefs, and relational dynamics that sustain symptoms.",
    icon: Search,
    color: "hsl(217, 91%, 60%)",     // blue
    bgClass: "bg-blue-500",
    textClass: "text-blue-500",
  },
  {
    num: 3,
    label: "Transpersonal Dive",
    shortLabel: "Transpersonal",
    description: "Engage deeper existential, spiritual, and collective-unconscious layers to unlock meaning and transformative insight.",
    icon: Compass,
    color: "hsl(271, 91%, 65%)",     // purple
    bgClass: "bg-purple-500",
    textClass: "text-purple-500",
  },
  {
    num: 4,
    label: "Actualization & Integration",
    shortLabel: "Integration",
    description: "Consolidate growth, embody new patterns in daily life, and cultivate sustained well-being and self-actualization.",
    icon: Star,
    color: "hsl(160, 84%, 39%)",     // emerald
    bgClass: "bg-emerald-500",
    textClass: "text-emerald-500",
  },
];

export default function HealingStagePanel({ healingStage }: Props) {
  const currentStage = healingStage.stage;

  return (
    <div className="space-y-3 md:space-y-4">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <Compass className="h-4 w-4 md:h-5 md:w-5 text-primary" />
        Healing Stage Assessment
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        Current position in the 5-stage healing framework
      </p>

      {/* Stepper — circles are fixed, connectors fill the space between */}
      <div className="flex items-center w-full px-1">
        {STAGES.map((s, i) => {
          const isActive = s.num === currentStage;
          const isPast = s.num < currentStage;
          const Icon = s.icon;

          return (
            <div key={s.num} className="contents">
              {/* Connector before (except first) */}
              {i > 0 && (
                <div
                  className="flex-1 h-0.5 min-w-2"
                  style={{
                    backgroundColor: isPast
                      ? "hsl(var(--muted-foreground) / 0.3)"
                      : "hsl(var(--muted))",
                  }}
                />
              )}
              {/* Circle */}
              <div className="flex flex-col items-center shrink-0">
                <div
                  className={`
                    w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all
                    ${isActive
                      ? "text-white scale-105 md:scale-110"
                      : isPast
                        ? "bg-muted-foreground/20 text-muted-foreground"
                        : "bg-muted text-muted-foreground/50"
                    }
                  `}
                  style={isActive ? {
                    backgroundColor: s.color,
                    boxShadow: `0 0 0 3px hsl(var(--card)), 0 0 0 5px ${s.color}44`,
                  } : undefined}
                >
                  <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </div>
                <span
                  className={`text-[9px] md:text-[10px] mt-1 md:mt-1.5 text-center leading-tight whitespace-nowrap ${
                    isActive ? `${s.textClass} font-semibold` : "text-muted-foreground/60"
                  }`}
                >
                  <span className="hidden md:inline">Stage {s.num}</span>
                  <span className="md:hidden">{s.num}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Current Stage Details */}
      <div className="rounded-lg border border-border p-3 md:p-4">
        <div className="flex items-center gap-2 mb-1.5 md:mb-2">
          <span className={`text-lg md:text-xl font-bold ${STAGES[currentStage]?.textClass}`}>
            Stage {currentStage}
          </span>
          <span className="text-xs md:text-sm font-semibold text-foreground">
            — {healingStage.stage_name}
          </span>
        </div>
        <p className="text-xs md:text-sm text-muted-foreground leading-relaxed mb-2">
          {healingStage.rationale}
        </p>
        <p className="text-[10px] md:text-xs text-muted-foreground/70 italic leading-relaxed">
          {STAGES[currentStage]?.description}
        </p>
      </div>
    </div>
  );
}
